import cx from 'classnames';

import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import uniqueId from 'lodash/uniqueId';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DivTooltip } from '@wg/wows-react-uikit';

import { getLastRegularSeason } from '~/helpers/ladder';
import { getTypeOfViewportSize } from '~/helpers/viewport';

import styles from './ClanLeagueSign.scss';
import ClanLeagueSignDivisionNumber from './ClanLeagueSignDivisionNumber';
import ClanLeagueSignTooltip from './ClanLeagueSignTooltip';

import { getImagePath } from './clanLeagueSign.utils';

import type { RatingsStage, WowsLadderRatingTeam } from '~/Actions/ActionClanProfile';
import type {
  ILadderSeasonLeague,
  LadderSeasonLeagueDivision,
  LadderSeasonAdditionalIcons,
  LadderSeasonLeagueIcons,
} from '~/store/slices/settingsSlice';
import type { Nullable } from '~/types/generics';

const progressCircleRadius = 50;
const lastSeason = getLastRegularSeason();
const lastSeasonId = lastSeason ? lastSeason.id : 0;

export type ClanLeagueSignProps = {
  leadingTeamNumber?: number;
  teamsRating?: Nullable<WowsLadderRatingTeam[]>;
  clanDivisionRating: number;
  clanMaxDivisionRating: number;
  clanDivision?: number;
  clanLeague?: number;
  clanStage?: Nullable<RatingsStage>;
  isFullSize?: boolean;
  fewTeams?: boolean;
};

type ImageSizeState = {
  leagueIcon: keyof LadderSeasonLeagueIcons;
  ribbon: keyof LadderSeasonLeagueIcons;
  ribbonWide: keyof LadderSeasonLeagueIcons;
  divisionIcon: keyof LadderSeasonLeagueDivision['icons'];
  progressStub: keyof LadderSeasonLeagueIcons;
  stars: string;
  viewportSizeType: string;
};

const DEFAULT_SIZE_STATE: ImageSizeState = {
  leagueIcon: 'default',
  ribbon: 'ribbon',
  ribbonWide: 'ribbon_wide',
  divisionIcon: 'default',
  progressStub: 'progress_stub',
  stars: '',
  viewportSizeType: '',
};

const SMALL_SIZE_STATE: ImageSizeState = {
  leagueIcon: 'small',
  ribbon: 'ribbon_small',
  ribbonWide: 'ribbon_wide_small',
  divisionIcon: 'small',
  progressStub: 'progress_stub_small',
  stars: '_small',
  viewportSizeType: 'small',
};

const ClanLeagueSign = ({
  leadingTeamNumber,
  teamsRating,
  clanDivisionRating,
  clanMaxDivisionRating,
  clanDivision,
  clanLeague,
  clanStage,
  isFullSize = false,
  fewTeams,
}: ClanLeagueSignProps) => {
  const seasonInfo = lastSeason;
  const tooltipId = useMemo(() => uniqueId('clan-league-sign_'), []);

  const [img, setImg] = useState<ImageSizeState>(DEFAULT_SIZE_STATE);

  const setImagesSize = useCallback(() => {
    const viewportSizeType = getTypeOfViewportSize();

    setImg((prev) => {
      if (viewportSizeType === prev.viewportSizeType) {
        return prev;
      }

      if (viewportSizeType === 'small' || viewportSizeType === 'medium') {
        return {
          ...SMALL_SIZE_STATE,
          viewportSizeType,
        };
      }

      if (viewportSizeType === 'large' || viewportSizeType === 'extra') {
        return {
          ...DEFAULT_SIZE_STATE,
          viewportSizeType,
        };
      }

      return { ...prev, viewportSizeType };
    });
  }, []);

  useEffect(() => {
    if (isFullSize) {
      return;
    }

    const onResize = () => setImagesSize();

    window.addEventListener('resize', onResize);
    setImagesSize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isFullSize, setImagesSize]);

  const divisionIndex = useMemo(() => {
    if (!isNumber(clanLeague) || isEmpty(seasonInfo)) {
      return null;
    }

    const league = seasonInfo?.leagues?.[clanLeague];
    if (!league) {
      return null;
    }

    const divisions = league.divisions;
    const numberOfDivisions = divisions ? divisions.length : 0;

    return clanDivision ? numberOfDivisions - clanDivision : null;
  }, [clanLeague, clanDivision, seasonInfo]);

  const getStage = useCallback(() => {
    if (!clanStage) {
      return 'progress';
    }
    return clanStage.type;
  }, [clanStage]);

  const getPromotionStarIconStyle = useCallback(
    (result: string): React.CSSProperties => {
      const additionalIcons = seasonInfo?.additional_icons
        ? seasonInfo.additional_icons
        : ({} as LadderSeasonAdditionalIcons);
      const starIcons = additionalIcons?.stars;
      const currentStar = `${result}${img.stars}` as keyof LadderSeasonAdditionalIcons['stars'];

      if (!starIcons) {
        return {};
      }

      return {
        backgroundImage: `url("${getImagePath(starIcons[currentStar])}")`,
      };
    },
    [img.stars, seasonInfo],
  );

  const renderQualificationProgress = useCallback(() => {
    if (!clanStage) {
      return null;
    }

    const victoriesRequired = clanStage.battles;
    const battlesProgress = clanStage.progress || [];

    if (!victoriesRequired || !battlesProgress) {
      return null;
    }

    const amountOfUnknownResults = victoriesRequired - battlesProgress.length;
    const unknownResults = amountOfUnknownResults > 0 ? new Array(amountOfUnknownResults).fill('empty') : [];
    const fullBattlesProgress = battlesProgress.concat(unknownResults);

    const starClassNames = isFullSize ? cx(styles.star, styles.starFullSize) : styles.star;
    const infoBoxStarsClassNames = isFullSize
      ? cx(styles.infoBoxStars, styles.infoBoxStarsFullSize)
      : styles.infoBoxStars;

    return (
      <ul className={infoBoxStarsClassNames}>
        {fullBattlesProgress.map((result, index) => (
          <li key={index} className={starClassNames} style={getPromotionStarIconStyle(String(result))} />
        ))}
      </ul>
    );
  }, [clanStage, getPromotionStarIconStyle, isFullSize]);

  const renderProgressStub = useCallback(() => {
    const league = isNumber(clanLeague) ? seasonInfo?.leagues?.[clanLeague] : undefined;
    if (!league) {
      return null;
    }

    const leagueIcons = league.icons;
    const progressBarStubIcon = leagueIcons?.[img.progressStub];

    const progressBarStubStyle = {
      backgroundImage: `url('${getImagePath(progressBarStubIcon)}')`,
    };

    const progressClassName = isFullSize ? cx(styles.progressStub, styles.progressStubSizeFull) : styles.progressStub;

    return <div style={progressBarStubStyle} className={progressClassName} />;
  }, [clanLeague, img.progressStub, isFullSize, seasonInfo]);

  const renderProgressBar = useCallback(() => {
    const stage = getStage();
    if (stage === 'qualification') {
      return null;
    }

    let firstLeague: ILadderSeasonLeague | undefined = undefined;

    if (lastSeason?.id === 301) {
      // REMOVE AFTER VERIZON CUP! 01.11.2020
      firstLeague = lastSeason.leagues[4];
    } else {
      firstLeague = lastSeason?.leagues?.[0] || lastSeason?.leagues?.[1];
    }

    if (firstLeague && firstLeague.number === clanLeague && firstLeague.divisions?.[0]?.number === clanDivision) {
      return renderProgressStub();
    }

    const maxRating = clanMaxDivisionRating;
    const clanRating = clanDivisionRating;

    if (!(typeof maxRating === 'number') || !(typeof clanRating === 'number')) {
      return null;
    }

    const clanLeagueObj = isNumber(clanLeague) ? seasonInfo?.leagues?.[clanLeague] : undefined;
    if (!clanLeagueObj) {
      return null;
    }

    let clanColor = clanLeagueObj.progress_color;
    if (!clanColor) {
      clanColor = clanLeagueObj.color || '#fff';
    }

    const progressBarLength = 2 * Math.PI * progressCircleRadius;
    const clanProgress = clanRating / maxRating;
    const progressBarOffset = progressBarLength * (1 - clanProgress);

    const progressStyle = {
      strokeDasharray: progressBarLength,
      strokeDashoffset: progressBarOffset,
      stroke: clanColor,
    };

    return (
      <div className={styles.progressChart}>
        <svg className={styles.progress} version="1.1" xmlns="http://www.w3.org/2000/svg">
          <circle className={styles.progressValue} style={progressStyle} cx="50%" cy="50%" r="calc(50% - 3px)" />
        </svg>
      </div>
    );
  }, [clanDivision, clanDivisionRating, clanLeague, clanMaxDivisionRating, getStage, renderProgressStub, seasonInfo]);

  const renderInfoBox = useCallback(() => {
    const stage = getStage();
    const isPromotion = stage !== 'progress';

    let rating = clanDivisionRating;
    if (rating < 0) {
      rating = 0;
    }

    const valueClassName = isFullSize ? cx(styles.infoBoxValue, styles.infoBoxValueFull) : styles.infoBoxValue;

    const classNameInfoBox = isFullSize
      ? cx(styles.clanInfoBoxWrapper, styles.clanInfoBoxWrapperFull)
      : styles.clanInfoBoxWrapper;

    const league = isNumber(clanLeague) ? seasonInfo?.leagues?.[clanLeague] : undefined;
    if (!league) {
      return null;
    }

    const ribbonType = stage === 'progress' ? league.icons?.[img.ribbon] : league.icons?.[img.ribbonWide];

    const ribbonStyle = ribbonType
      ? {
          backgroundImage: `url('${getImagePath(ribbonType)}')`,
        }
      : undefined;

    return isPromotion ? (
      <div className={classNameInfoBox} style={ribbonStyle}>
        {renderQualificationProgress()}
      </div>
    ) : (
      <div className={classNameInfoBox} style={ribbonStyle}>
        <div className={valueClassName}>{rating}</div>
      </div>
    );
  }, [
    clanDivisionRating,
    clanLeague,
    getStage,
    img.ribbon,
    img.ribbonWide,
    isFullSize,
    renderQualificationProgress,
    seasonInfo?.leagues,
  ]);

  if (isEmpty(seasonInfo)) {
    return null;
  }

  const stage = getStage();
  if (stage === 'qualification') {
    return null;
  }

  const league = isNumber(clanLeague) ? seasonInfo?.leagues?.[clanLeague] : undefined;
  if (!league) {
    return null;
  }

  const background = getImagePath(league.icons?.[img.leagueIcon]);
  const backgroundIconStyles = { backgroundImage: `url('${background}')` };

  const isShowProgressBar = isFullSize && stage === 'progress';
  const signSizeClass = isFullSize ? styles.fullSize : styles.normalSize;
  const classNameBase = cx(styles.base, signSizeClass);

  return (
    <DivTooltip
      tooltipBody={
        <ClanLeagueSignTooltip
          clanDivision={clanDivision}
          clanLeague={clanLeague}
          isFullSize={isFullSize}
          leadingTeamNumber={leadingTeamNumber}
          fewTeams={fewTeams}
          teamsRating={teamsRating}
          lastSeason={lastSeason}
          lastSeasonId={lastSeasonId}
        />
      }
    >
      <div style={backgroundIconStyles} className={classNameBase} data-tip data-for={tooltipId}>
        {isShowProgressBar ? renderProgressBar() : renderProgressStub()}
        {renderInfoBox()}
        <ClanLeagueSignDivisionNumber
          clanDivision={clanDivision}
          clanLeague={clanLeague}
          divisionIndex={divisionIndex}
          img={img}
          isFullSize={isFullSize}
          seasonInfo={seasonInfo}
        />
      </div>
    </DivTooltip>
  );
};

export default memo(ClanLeagueSign);
